// Unified Image ID System
// This utility provides consistent image identification across the entire application

/**
 * Generate unique image ID based on content metadata + timestamp
 * Format: img_{timestamp}_{size}_{randomSuffix}
 */
export function generateImageId(imageBase64: string): string {
  const timestamp = Date.now();
  const size = imageBase64.length;
  const randomSuffix = Math.random().toString(36).substr(2, 5);
  return `img_${timestamp}_${size}_${randomSuffix}`;
}

/**
 * Generate consistent filename for downloads
 * Format: pfpmaker_{imageId}.png
 */
export function generateImageFilename(imageId: string, toolId?: string): string {
  return `pfpmaker_${imageId}.png`;
}

/**
 * Extract metadata from image ID
 * Useful for debugging and analytics
 */
export function parseImageId(imageId: string) {
  const parts = imageId.split('_');
  if (parts.length !== 4 || parts[0] !== 'img') {
    return null; // Invalid format
  }

  const [prefix, timestamp, size, suffix] = parts;
  return {
    timestamp: parseInt(timestamp),
    size: parseInt(size),
    suffix,
    generatedAt: new Date(parseInt(timestamp)),
  };
}

/**
 * Check if an image ID is valid
 */
export function isValidImageId(imageId: string): boolean {
  return parseImageId(imageId) !== null;
}

/**
 * Generate a short display name for an image ID
 * Useful for UI components that need to show image references
 */
export function getImageDisplayName(imageId: string): string {
  const parsed = parseImageId(imageId);
  if (!parsed) return 'Unknown Image';

  const time = parsed.generatedAt.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });
  return `Image ${time}`;
}

/**
 * Image Format Conversion Utilities
 * Handle consistent conversion between data URLs and base64 strings
 */

/**
 * Strip data URL prefix to get raw base64 string
 * @param dataUrl - Image data URL (data:image/...;base64,XXX) or raw base64
 * @returns Raw base64 string
 */
export function stripDataUrlPrefix(dataUrl: string): string {
  if (!dataUrl) return '';
  return dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
}

/**
 * Add data URL prefix to base64 string
 * @param base64 - Raw base64 string or data URL
 * @param mimeType - MIME type (defaults to image/png)
 * @returns Complete data URL
 */
export function addDataUrlPrefix(base64: string, mimeType: string = 'image/png'): string {
  if (!base64) return '';
  return base64.startsWith('data:') ? base64 : `data:${mimeType};base64,${base64}`;
}

/**
 * Ensure image is in the correct format
 * @param image - Image data in any format
 * @param needsPrefix - Whether the output should have data URL prefix
 * @param mimeType - MIME type for prefix (defaults to image/png)
 * @returns Image in requested format
 */
export function ensureImageFormat(image: string, needsPrefix: boolean, mimeType: string = 'image/png'): string {
  if (!image) return '';
  return needsPrefix ? addDataUrlPrefix(image, mimeType) : stripDataUrlPrefix(image);
}
